// ============================================================
// SoundManager -- procedural sound effects via Web Audio API
// ============================================================
//
// All sounds are synthesised at runtime with oscillators and noise
// buffers. No audio files are needed.
// ============================================================

export class SoundManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;

  // Persistent engine hum nodes (created once, pitch updated every frame)
  private engineOsc: OscillatorNode | null = null;
  private engineOsc2: OscillatorNode | null = null; // detuned wobble for cartoony feel
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private engineStarted = false;

  // Background music
  private musicAudio: HTMLAudioElement | null = null;
  private musicVolume = 0.25;

  private muted = false;

  // Keep master volume moderate so synth sounds are not jarring
  private static MASTER_VOLUME = 0.3;
  private volume = 1.0; // user-controlled volume multiplier (0–1)

  // Map-specific music tracks
  private static MUSIC_TRACKS: Record<string, string> = {
    candy_world: '/audio/candy-world.mp3',
    giant_bathroom: '/audio/giant-bathroom.mp3',
    desert_canyon: '/audio/desert-canyon.mp3',
    space_station: '/audio/space-station.mp3',
    haunted_mansion: '/audio/haunted-mansion.mp3',
    ice_world: '/audio/ice-world.mp3',
  };
  private static MENU_MUSIC = '/audio/menu.mp3';

  // ────────────────────────────────────────────────────────────
  // Initialisation (must be called after a user gesture)
  // ────────────────────────────────────────────────────────────

  init(): void {
    if (this.ctx) return; // already initialised

    this.ctx = new AudioContext();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = SoundManager.MASTER_VOLUME;
    this.masterGain.connect(this.ctx.destination);

    // Resume in case the browser auto-suspended the context
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  // ────────────────────────────────────────────────────────────
  // Engine hum (continuous, pitch tracks kart speed)
  // ────────────────────────────────────────────────────────────

  startEngine(): void {
    if (!this.ctx || !this.masterGain || this.engineStarted) return;

    // Triangle oscillator + detuned wobble -> low-pass filter -> gain -> master
    // Triangle wave is softer/rounder than sawtooth for a cartoony hum
    this.engineOsc = this.ctx.createOscillator();
    this.engineOsc.type = 'triangle';
    this.engineOsc.frequency.value = 80; // idle frequency (higher = cartoonier)

    // Second oscillator slightly detuned for a wobbly cartoon engine feel
    this.engineOsc2 = this.ctx.createOscillator();
    this.engineOsc2.type = 'triangle';
    this.engineOsc2.frequency.value = 80;
    this.engineOsc2.detune.value = 15; // slight detune for thickness

    this.engineFilter = this.ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 400;
    this.engineFilter.Q.value = 1;

    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0.12; // much quieter than before (was 0.35)

    this.engineOsc.connect(this.engineFilter);
    this.engineOsc2.connect(this.engineFilter);
    this.engineFilter.connect(this.engineGain);
    this.engineGain.connect(this.masterGain);

    this.engineOsc.start();
    this.engineOsc2.start();
    this.engineStarted = true;
  }

  stopEngine(): void {
    if (this.engineOsc) {
      try { this.engineOsc.stop(); } catch { /* already stopped */ }
      this.engineOsc.disconnect();
      this.engineOsc = null;
    }
    if (this.engineOsc2) {
      try { this.engineOsc2.stop(); } catch { /* already stopped */ }
      this.engineOsc2.disconnect();
      this.engineOsc2 = null;
    }
    if (this.engineFilter) {
      this.engineFilter.disconnect();
      this.engineFilter = null;
    }
    if (this.engineGain) {
      this.engineGain.disconnect();
      this.engineGain = null;
    }
    this.engineStarted = false;
  }

  /** Call every frame to slide the engine pitch with kart speed. */
  updateEngine(speed: number, maxSpeed: number): void {
    if (!this.engineOsc || !this.engineFilter || !this.ctx) return;

    // Map speed 0..maxSpeed to frequency 80..240 Hz (higher range = cartoonier)
    const t = Math.abs(speed) / maxSpeed; // 0-1 (can exceed 1 during boost)
    const freq = 80 + Math.min(t, 1.5) * 160;
    const filterFreq = 400 + Math.min(t, 1.5) * 600;

    // Use setTargetAtTime for smooth interpolation (avoids clicks)
    const now = this.ctx.currentTime;
    this.engineOsc.frequency.setTargetAtTime(freq, now, 0.05);
    this.engineOsc2?.frequency.setTargetAtTime(freq, now, 0.05);
    this.engineFilter.frequency.setTargetAtTime(filterFreq, now, 0.05);
  }

  // ────────────────────────────────────────────────────────────
  // Boost activation -- ascending frequency sweep
  // ────────────────────────────────────────────────────────────

  playBoost(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(900, now + 0.25);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.exponentialRampToValueAtTime(2000, now + 0.25);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.4);
    osc.onended = () => { osc.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Drift boost -- ascending whoosh, intensity scales by tier
  // ────────────────────────────────────────────────────────────

  playDriftBoost(tier: 'orange' | 'red' | 'purple'): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;

    // Tier parameters: duration, pitch range, volume, filter ceiling
    const params = {
      orange: { dur: 0.2, freqStart: 200, freqEnd: 500, vol: 0.12, filterEnd: 1200 },
      red:    { dur: 0.35, freqStart: 250, freqEnd: 800, vol: 0.18, filterEnd: 2000 },
      purple: { dur: 0.5, freqStart: 300, freqEnd: 1200, vol: 0.22, filterEnd: 3000 },
    };
    const p = params[tier];

    // Layer 1: Filtered noise whoosh
    const sampleRate = this.ctx.sampleRate;
    const noiseLen = Math.ceil(sampleRate * p.dur);
    const noiseBuf = this.ctx.createBuffer(1, noiseLen, sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }

    const noiseSrc = this.ctx.createBufferSource();
    noiseSrc.buffer = noiseBuf;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(400, now);
    filter.frequency.exponentialRampToValueAtTime(p.filterEnd, now + p.dur * 0.8);
    filter.Q.value = 1.5;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(p.vol, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + p.dur);

    noiseSrc.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.masterGain);

    noiseSrc.start(now);
    noiseSrc.stop(now + p.dur + 0.01);
    noiseSrc.onended = () => { noiseSrc.disconnect(); filter.disconnect(); noiseGain.disconnect(); };

    // Layer 2: Oscillator sweep for tonal character
    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(p.freqStart, now);
    osc.frequency.exponentialRampToValueAtTime(p.freqEnd, now + p.dur * 0.7);

    const oscGain = this.ctx.createGain();
    oscGain.gain.setValueAtTime(p.vol * 0.6, now);
    oscGain.gain.exponentialRampToValueAtTime(0.01, now + p.dur);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + p.dur + 0.01);
    osc.onended = () => { osc.disconnect(); oscGain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Item pickup -- cheerful "ding"
  // ────────────────────────────────────────────────────────────

  playItemPickup(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(1100, now + 0.06);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + 0.25);
    osc.onended = () => { osc.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Coin pickup -- short bright ascending chime (C6 -> E6)
  // ────────────────────────────────────────────────────────────

  playCoinPickup(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;

    // Note 1: C6 (1046.5 Hz)
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 1046.5;

    const gain1 = this.ctx.createGain();
    gain1.gain.setValueAtTime(0.3, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    osc1.connect(gain1);
    gain1.connect(this.masterGain);

    osc1.start(now);
    osc1.stop(now + 0.12);
    osc1.onended = () => { osc1.disconnect(); gain1.disconnect(); };

    // Note 2: E6 (1318.5 Hz) slightly delayed
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 1318.5;

    const gain2 = this.ctx.createGain();
    gain2.gain.setValueAtTime(0.0, now);
    gain2.gain.setValueAtTime(0.3, now + 0.06);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.18);

    osc2.connect(gain2);
    gain2.connect(this.masterGain);

    osc2.start(now + 0.06);
    osc2.stop(now + 0.2);
    osc2.onended = () => { osc2.disconnect(); gain2.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Collision / stun -- white noise burst through a low-pass
  // ────────────────────────────────────────────────────────────

  playCollision(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.2;

    // Generate a short buffer of white noise
    const sampleRate = this.ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(500, now);
    filter.frequency.exponentialRampToValueAtTime(100, now + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + duration + 0.01);
    source.onended = () => { source.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Countdown beep (3, 2, 1 = lower tone, GO! = higher tone)
  // ────────────────────────────────────────────────────────────

  playCountdownBeep(isGo: boolean): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const freq = isGo ? 880 : 440;
    const dur = isGo ? 0.3 : 0.15;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + dur + 0.01);
    osc.onended = () => { osc.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Lap complete -- three ascending chime notes
  // ────────────────────────────────────────────────────────────

  playLapComplete(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    const noteGap = 0.1;
    const noteDur = 0.12;

    for (let i = 0; i < notes.length; i++) {
      const t = now + i * noteGap;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = notes[i];

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + noteDur);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(t);
      osc.stop(t + noteDur + 0.01);
      osc.onended = () => { osc.disconnect(); gain.disconnect(); };
    }
  }

  // ────────────────────────────────────────────────────────────
  // Barrel roll -- whoosh + ascending sparkle chime
  // ────────────────────────────────────────────────────────────

  playBarrelRoll(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.6;

    // Layer 1: Filtered noise "whoosh" sweep
    const sampleRate = this.ctx.sampleRate;
    const noiseLen = Math.ceil(sampleRate * duration);
    const noiseBuf = this.ctx.createBuffer(1, noiseLen, sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      noiseData[i] = Math.random() * 2 - 1;
    }

    const noiseSrc = this.ctx.createBufferSource();
    noiseSrc.buffer = noiseBuf;

    const bandpass = this.ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.setValueAtTime(300, now);
    bandpass.frequency.exponentialRampToValueAtTime(2000, now + duration * 0.7);
    bandpass.frequency.exponentialRampToValueAtTime(800, now + duration);
    bandpass.Q.value = 2;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0, now);
    noiseGain.gain.linearRampToValueAtTime(0.2, now + 0.08);
    noiseGain.gain.linearRampToValueAtTime(0.15, now + duration * 0.6);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    noiseSrc.connect(bandpass);
    bandpass.connect(noiseGain);
    noiseGain.connect(this.masterGain);

    noiseSrc.start(now);
    noiseSrc.stop(now + duration + 0.01);
    noiseSrc.onended = () => { noiseSrc.disconnect(); bandpass.disconnect(); noiseGain.disconnect(); };

    // Layer 2: Ascending shimmer tone (triangle wave sweep)
    const shimmer = this.ctx.createOscillator();
    shimmer.type = 'triangle';
    shimmer.frequency.setValueAtTime(400, now);
    shimmer.frequency.exponentialRampToValueAtTime(1200, now + duration * 0.8);

    const shimmerGain = this.ctx.createGain();
    shimmerGain.gain.setValueAtTime(0.08, now);
    shimmerGain.gain.linearRampToValueAtTime(0.15, now + duration * 0.5);
    shimmerGain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    shimmer.connect(shimmerGain);
    shimmerGain.connect(this.masterGain);

    shimmer.start(now);
    shimmer.stop(now + duration + 0.01);
    shimmer.onended = () => { shimmer.disconnect(); shimmerGain.disconnect(); };

    // Layer 3: Landing "ding" chime at the end
    const chimeDelay = duration - 0.12;
    const chime = this.ctx.createOscillator();
    chime.type = 'sine';
    chime.frequency.setValueAtTime(1320, now + chimeDelay); // E6

    const chimeGain = this.ctx.createGain();
    chimeGain.gain.setValueAtTime(0.0, now);
    chimeGain.gain.setValueAtTime(0.3, now + chimeDelay);
    chimeGain.gain.exponentialRampToValueAtTime(0.01, now + chimeDelay + 0.25);

    chime.connect(chimeGain);
    chimeGain.connect(this.masterGain);

    chime.start(now + chimeDelay);
    chime.stop(now + chimeDelay + 0.3);
    chime.onended = () => { chime.disconnect(); chimeGain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Lightning strike -- heavy crack/thunder
  // ────────────────────────────────────────────────────────────

  playLightningStrike(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.4;

    // White noise burst with sharp attack
    const sampleRate = this.ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = noiseBuffer;

    // Lowpass filter sweep from high to low (crack -> rumble)
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(4000, now);
    filter.frequency.exponentialRampToValueAtTime(80, now + duration);
    filter.Q.value = 1;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.5, now);             // sharp attack
    gain.gain.setValueAtTime(0.4, now + 0.02);      // sustain briefly
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + duration + 0.01);
    source.onended = () => { source.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Star jingle -- bright ascending pentatonic arpeggio
  // ────────────────────────────────────────────────────────────

  playStarJingle(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    // Pentatonic scale: C6, D6, E6, G6, A6, C7
    const notes = [1046.5, 1174.7, 1318.5, 1568.0, 1760.0, 2093.0];
    const noteGap = 0.07;
    const noteDur = 0.1;

    for (let i = 0; i < notes.length; i++) {
      const t = now + i * noteGap;

      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = notes[i];

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.25, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + noteDur);

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(t);
      osc.stop(t + noteDur + 0.01);
      osc.onended = () => { osc.disconnect(); gain.disconnect(); };
    }
  }

  // ────────────────────────────────────────────────────────────
  // Blue shell warning -- ominous descending tone with tremolo
  // ────────────────────────────────────────────────────────────

  playBlueShellWarning(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.5;

    // Low frequency descending sweep
    const osc = this.ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + duration);

    // Tremolo via LFO modulating gain
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 8; // 8 Hz tremolo

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.1; // tremolo depth

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    // Lowpass to tame the sawtooth harshness
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 600;
    filter.Q.value = 1;

    // LFO -> lfoGain -> gain.gain (AM tremolo)
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    lfo.start(now);
    osc.stop(now + duration + 0.01);
    lfo.stop(now + duration + 0.01);
    osc.onended = () => {
      osc.disconnect(); filter.disconnect(); gain.disconnect();
      lfo.disconnect(); lfoGain.disconnect();
    };
  }

  // ────────────────────────────────────────────────────────────
  // Blooper ink -- squelchy wet noise burst
  // ────────────────────────────────────────────────────────────

  playBlooperInk(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.25;

    // Noise burst through resonant bandpass for wet squelch
    const sampleRate = this.ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = noiseBuffer;

    // Resonant bandpass for "wet" squelch character
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.exponentialRampToValueAtTime(300, now + duration);
    filter.Q.value = 8; // high resonance for squelchy tone

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + duration + 0.01);
    source.onended = () => { source.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Wrong way beep -- short warning square wave
  // ────────────────────────────────────────────────────────────

  playWrongWayBeep(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const dur = 0.1;

    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 440;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + dur + 0.01);
    osc.onended = () => { osc.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Shell fire -- quick pop/launch sound
  // ────────────────────────────────────────────────────────────

  playShellFire(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.12;

    // Short noise burst with high-pass filter for a "pop"
    const sampleRate = this.ctx.sampleRate;
    const length = Math.ceil(sampleRate * duration);
    const noiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.frequency.exponentialRampToValueAtTime(800, now + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    source.start(now);
    source.stop(now + duration + 0.01);
    source.onended = () => { source.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Banana place -- soft plop/drop sound
  // ────────────────────────────────────────────────────────────

  playBananaPlace(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const dur = 0.15;

    // Quick low-frequency sine with fast decay for a soft "plop"
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + dur);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + dur);

    osc.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + dur + 0.01);
    osc.onended = () => { osc.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Blooper squirt alias (maps to playBlooperInk)
  // ────────────────────────────────────────────────────────────

  playBlooperSquirt(): void {
    this.playBlooperInk();
  }

  // ────────────────────────────────────────────────────────────
  // Wrong way alert alias (maps to playWrongWayBeep)
  // ────────────────────────────────────────────────────────────

  playWrongWayAlert(): void {
    this.playWrongWayBeep();
  }

  // ────────────────────────────────────────────────────────────
  // Start boost -- rev-up engine sweep (triangle 100->500Hz, 0.3s)
  // ────────────────────────────────────────────────────────────

  playStartBoost(): void {
    if (!this.ctx || !this.masterGain || this.muted) return;

    const now = this.ctx.currentTime;
    const duration = 0.3;

    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(100, now);
    osc.frequency.exponentialRampToValueAtTime(500, now + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration + 0.1);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.exponentialRampToValueAtTime(1500, now + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);

    osc.start(now);
    osc.stop(now + duration + 0.15);
    osc.onended = () => { osc.disconnect(); filter.disconnect(); gain.disconnect(); };
  }

  // ────────────────────────────────────────────────────────────
  // Star jingle (looping version) -- returns a stop function
  // ────────────────────────────────────────────────────────────

  playStarJingleLoop(): () => void {
    if (!this.ctx || !this.masterGain || this.muted) {
      return () => {};
    }

    const ctx = this.ctx;
    const master = this.masterGain;
    let stopped = false;

    const notes = [523.25, 659.25, 783.99, 1046.5];
    const noteLen = 0.12;
    const cycleLen = notes.length * noteLen;

    const groupGain = ctx.createGain();
    groupGain.gain.value = 0.2;
    groupGain.connect(master);

    const oscillators: OscillatorNode[] = [];

    function playLoop() {
      if (stopped) return;
      const now = ctx.currentTime;
      for (let i = 0; i < notes.length; i++) {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = notes[i];

        const noteGain = ctx.createGain();
        const t = now + i * noteLen;
        noteGain.gain.setValueAtTime(0.0, t);
        noteGain.gain.linearRampToValueAtTime(0.2, t + 0.02);
        noteGain.gain.linearRampToValueAtTime(0.0, t + noteLen);

        osc.connect(noteGain);
        noteGain.connect(groupGain);

        osc.start(t);
        osc.stop(t + noteLen + 0.01);
        oscillators.push(osc);

        osc.onended = () => {
          osc.disconnect();
          noteGain.disconnect();
        };
      }
      setTimeout(playLoop, cycleLen * 1000);
    }

    playLoop();

    return () => {
      stopped = true;
      groupGain.gain.setValueAtTime(0, ctx.currentTime);
      groupGain.disconnect();
      for (const osc of oscillators) {
        try { osc.stop(); } catch { /* already stopped */ }
      }
    };
  }

  // ────────────────────────────────────────────────────────────
  // Background music
  // ────────────────────────────────────────────────────────────

  /** Play menu screen music (looping). */
  playMenuMusic(): void {
    this._playMusic(SoundManager.MENU_MUSIC);
  }

  /** Play map-specific racing music (looping). */
  playMapMusic(mapId: string): void {
    const track = SoundManager.MUSIC_TRACKS[mapId];
    if (track) {
      this._playMusic(track);
    }
  }

  /** Stop any currently playing music. */
  stopMusic(): void {
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.musicAudio.currentTime = 0;
      this.musicAudio = null;
    }
  }

  private _playMusic(src: string): void {
    this.stopMusic();
    this.musicAudio = new Audio(src);
    this.musicAudio.loop = true;
    this.musicAudio.volume = this.muted ? 0 : this.musicVolume;
    this.musicAudio.play().catch(() => {
      // Autoplay blocked -- will play once user interacts
    });
  }

  // ────────────────────────────────────────────────────────────
  // Mute / unmute
  // ────────────────────────────────────────────────────────────

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolume();
  }

  getMuted(): boolean {
    return this.muted;
  }

  /** Set user-controlled volume (0–1). Persisted via the UI slider. */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyVolume();
  }

  getVolume(): number {
    return this.volume;
  }

  private applyVolume(): void {
    const effective = this.muted ? 0 : SoundManager.MASTER_VOLUME * this.volume;
    if (this.masterGain) {
      this.masterGain.gain.value = effective;
    }
    if (this.musicAudio) {
      this.musicAudio.volume = this.muted ? 0 : this.musicVolume * this.volume;
    }
  }

  // ────────────────────────────────────────────────────────────
  // Disposal
  // ────────────────────────────────────────────────────────────

  dispose(): void {
    this.stopEngine();
    this.stopMusic();
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
    this.masterGain = null;
  }
}
